import Component from '@glimmer/component';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import Button from 'tio-ui/components/button';
import type RouterService from '@ember/routing/router-service';
import type { ElfTileState } from 'tio-employee/utils/lrap-enrollment';

interface ElfTileSignature {
  Args: {
    state: ElfTileState;
  };
  Element: HTMLDivElement;
}

/**
 * The SLR dashboard's ELF (Employment Loan Forgiveness / LRAP) enrollment
 * tile — the pre-enrollment entry point per the Figma "ELF (LRAP)" designs.
 * Two states:
 * - `blank` ("Need to enroll"): benefit pitch + Enroll Now;
 * - `in-progress` ("Started but needs to finish"): progress note + Continue
 *   Enrollment, reachable once the backend enrollment state machine reports
 *   `pending_signature`.
 * Enrolled users never see this tile — the dashboard hides it and shows the
 * regular SLR dashboard with an Enrolled chip instead.
 */
export default class ElfTile extends Component<ElfTileSignature> {
  @service declare router: RouterService;

  goToEnrollment = () => {
    this.router.transitionTo('authenticated.slr.lrap.enrollment');
  };

  get isInProgress() {
    return this.args.state === 'in-progress';
  }

  <template>
    <div class="tio-elf-tile" data-test-elf-tile={{@state}} ...attributes>
      <h2 class="tio-elf-tile__heading text-lg">{{t "lrap.enrollment.cta_title"}}</h2>
      <p class="tio-elf-tile__body">{{t "lrap.enrollment.cta_body"}}</p>
      {{#if this.isInProgress}}
        <p class="tio-elf-tile__progress" data-test-elf-tile-progress>
          {{t "lrap.enrollment.tile.in_progress"}}
        </p>
      {{/if}}
      <div class="tio-elf-tile__actions">
        <Button
          @intent="primary"
          @appearance="outlined"
          {{on "click" this.goToEnrollment}}
          data-test-elf-tile-action
        >
          {{if
            this.isInProgress
            (t "lrap.enrollment.tile.continue_enrollment")
            (t "lrap.enrollment.tile.enroll_now")
          }}
        </Button>
      </div>
    </div>
  </template>
}
